import { Action, PathsV1CompanyGroupUuidGetParametersQueryExtend } from "@/api-schema-enums";
import { Can } from "@/casl-provider";
import {
  useCompanyGroupByUuidQuery,
  type CompanyGroupByUuidParameters,
  type CompanyGroupByUuidResponse,
} from "@/common/api/company-group/use-company-group-by-uuid.query";
import { DetailHeader } from "@/components/detail-page/detail-header";
import { DetailPageLayout } from "@/components/detail-page/detail-page-layout";
import { DetailPageSkeleton } from "@/components/detail-page/detail-page-skeleton";
import { DetailTabs } from "@/components/detail-page/detail-tabs";
import { SystemIdTab } from "@/components/detail-page/tabs/system-id-tab";
import type { DetailAction, DetailTab } from "@/components/detail-page/types";
import { CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import { getCompanyGroupPaperlessWorkspaceId } from "@/modules/masterdata/company-group/company-group-identifiers";
import { Building2, Fingerprint, Network, PencilIcon } from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";
import { CompaniesTab } from "./tabs/companies-tab/companies-tab";

const COMPANY_GROUP_DETAIL_QUERY_PARAMS = {
  parameters: { extend: [PathsV1CompanyGroupUuidGetParametersQueryExtend.CompanyGroupIdentifiers] },
};

const COMPANY_GROUP_DETAIL_TRACKING_NAME = "company-group";

export function CompanyGroupDetailView(): ReactElement {
  return (
    <Can I="read" a="CompanyGroup">
      <CompanyGroupDetailQuery />
    </Can>
  );
}

function CompanyGroupDetailQuery(): ReactElement {
  const { t } = useTranslation(["masterdata"]);
  const params = useParams<{ uuid: string }>();
  const uuid = params.uuid ?? "";
  const companyGroupQuery = useCompanyGroupByUuidQuery(uuid, COMPANY_GROUP_DETAIL_QUERY_PARAMS);

  if (companyGroupQuery.isPending) {
    return <DetailPageSkeleton />;
  }

  return (
    <UuidExistenceWrapper<CompanyGroupByUuidParameters, CompanyGroupByUuidResponse>
      uuid={uuid}
      query={useCompanyGroupByUuidQuery}
      queryResult={companyGroupQuery}
      notFoundProps={{
        title: t("masterdata:CompanyGroupNotFound"),
        message: t("masterdata:CompanyGroupDoesNotExist"),
        description: t("masterdata:CheckCompanyGroupIdOrContactSupport"),
        backTo: "~/masterdata/company-group",
        backToLabel: t("masterdata:BackToCompanyGroupOverview"),
      }}
    >
      {companyGroupQuery.data ? <CompanyGroupDetailContent companyGroup={companyGroupQuery.data} /> : null}
    </UuidExistenceWrapper>
  );
}

function CompanyGroupDetailContent({ companyGroup }: { companyGroup: CompanyGroupByUuidResponse }): ReactElement {
  const { t } = useTranslation(["masterdata", "common"]);
  const [, navigate] = useLocation();
  const paperlessWorkspaceId = getCompanyGroupPaperlessWorkspaceId(companyGroup);

  const actions: DetailAction[] = [
    {
      id: "edit",
      label: t("common:Edit"),
      icon: PencilIcon,
      permission: { action: Action.Manage, subject: "CompanyGroup" },
      onClick: () => navigate(`~/masterdata/company-group/${companyGroup.uuid}/update`),
    },
  ];

  const tabs: DetailTab[] = [
    {
      id: "companies",
      label: t("common:Companies"),
      icon: Building2,
      content: () => <CompaniesTab companyGroupUuid={companyGroup.uuid} />,
    },
    {
      id: "identifiers",
      label: t("masterdata:SystemIds"),
      icon: Fingerprint,
      content: () => (
        <SystemIdTab
          uuid={companyGroup.uuid}
          technicalId={companyGroup.technicalId}
          createdAt={companyGroup.createdAt}
          updatedAt={companyGroup.updatedAt}
          identifiers={
            paperlessWorkspaceId
              ? [{ id: "paperless", label: t("masterdata:PaperlessWorkspaceId"), value: paperlessWorkspaceId }]
              : []
          }
        />
      ),
    },
  ];

  return (
    <DetailPageLayout
      variant={CenteredLayoutVariant.Wide}
      header={<DetailHeader icon={<Network />} title={companyGroup.name} actions={actions} />}
    >
      <DetailTabs sections={tabs} trackingName={COMPANY_GROUP_DETAIL_TRACKING_NAME} />
    </DetailPageLayout>
  );
}
